import { Info, Upload, FileText, HelpCircle } from 'lucide-react'

export interface DynamicFormField {
  id: string
  name: string
  label: string
  type:
    | 'text'
    | 'textarea'
    | 'email'
    | 'phone'
    | 'number'
    | 'date'
    | 'checkbox'
    | 'radio'
    | 'select'
    | 'dropdown'
    | 'multiselect'
    | 'file'
    | 'image'
  placeholder?: string
  helpText?: string
  tooltip?: string
  required?: boolean
  options?: string[]
  // Admin stores these as flat strings (NOT nested objects)
  dependsOn?: string
  showIfValue?: string
}

export interface DynamicDocumentConfig {
  id: string
  name: string
  required: boolean
  acceptedFormats?: string[]
  maxMb?: number
}

interface DynamicFormRendererProps {
  fields: DynamicFormField[]
  documents?: DynamicDocumentConfig[]
  values: Record<string, any>
  onChange: (name: string, value: any) => void
  readOnly?: boolean
}

/**
 * Mirrors the admin FormBuilder live preview exactly.
 * Supports conditional visibility via dependsOn + showIfValue (flat string format).
 * Supports: text, textarea, dropdown, radio, checkbox, file, image, number, date.
 * Also renders the required verification documents upload section.
 *
 * Light/Dark mode: uses explicit Tailwind classes for high contrast in both themes.
 */
export function DynamicFormRenderer({
  fields,
  documents = [],
  values,
  onChange,
  readOnly = false,
}: DynamicFormRendererProps) {
  if ((!fields || fields.length === 0) && (!documents || documents.length === 0)) {
    return (
      <div className="p-6 text-center text-xs text-slate-500 dark:text-muted-foreground bg-slate-50 dark:bg-muted/30 rounded-xl border border-slate-200 dark:border-border/60">
        No custom fields configured for this application form. Admin has not set up this form yet.
      </div>
    )
  }

  // Evaluate conditional visibility — matches admin's isFieldVisibleInPreview logic exactly
  const isVisible = (field: DynamicFormField): boolean => {
    if (!field.dependsOn) return true
    const parentValue = values[field.dependsOn]
    if (!field.showIfValue) return !!parentValue
    return String(parentValue).toLowerCase() === String(field.showIfValue).toLowerCase()
  }

  const handleFileUpload = (fieldName: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const docMeta = {
      field: fieldName,
      fileName: file.name,
      url: URL.createObjectURL(file),
      uploadedAt: new Date().toISOString(),
      version: (values[fieldName]?.version || 0) + 1,
    }
    onChange(fieldName, docMeta)
  }

  const visibleFields = fields.filter(isVisible)

  // Shared input classes — high contrast in both light and dark
  const inputBase =
    'w-full rounded-lg border border-slate-300 dark:border-border/80 bg-white dark:bg-background text-slate-900 dark:text-foreground placeholder:text-slate-400 dark:placeholder:text-muted-foreground text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors disabled:opacity-60'

  return (
    <div className="space-y-3">
      {/* Dynamic Form Fields */}
      {visibleFields.map((field) => {
        const value = values[field.name] ?? ''
        const type = (field.type || 'text').toLowerCase()

        return (
          <div
            key={field.id || field.name}
            className="space-y-2 rounded-xl border border-slate-200 dark:border-border/70 bg-white dark:bg-card p-4 shadow-sm"
          >
            {/* Label row */}
            <div className="flex items-start justify-between gap-2">
              <label className="font-semibold text-slate-800 dark:text-foreground flex items-center gap-1.5 text-sm leading-snug">
                {field.label || 'Question'}
                {field.required && (
                  <span className="text-red-500 font-bold" title="Required">
                    *
                  </span>
                )}
              </label>
              {field.tooltip && (
                <span
                  className="shrink-0 text-blue-600 dark:text-blue-400 text-[11px] font-bold flex items-center gap-0.5 cursor-help border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10 rounded-md px-1.5 py-0.5"
                  title={field.tooltip}
                >
                  <HelpCircle className="size-3" /> Info
                </span>
              )}
            </div>

            {/* Help text */}
            {field.helpText && (
              <p className="text-xs text-slate-500 dark:text-muted-foreground leading-relaxed">
                {field.helpText}
              </p>
            )}

            {/* ── Input Controls ── match admin preview exactly ── */}

            {/* Text / email / phone / number / date */}
            {(type === 'text' ||
              type === 'email' ||
              type === 'phone' ||
              type === 'number' ||
              type === 'date') && (
              <input
                type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
                value={value}
                disabled={readOnly}
                onChange={(e) => onChange(field.name, e.target.value)}
                placeholder={field.placeholder || `Enter ${field.label}...`}
                required={field.required}
                className={inputBase}
              />
            )}

            {/* Textarea */}
            {type === 'textarea' && (
              <textarea
                rows={3}
                value={value}
                disabled={readOnly}
                onChange={(e) => onChange(field.name, e.target.value)}
                placeholder={field.placeholder || `Enter ${field.label}...`}
                required={field.required}
                className={`${inputBase} resize-none`}
              />
            )}

            {/* Dropdown / Select — exact match to admin "-- Choose Option --" */}
            {(type === 'dropdown' || type === 'select') && (
              <select
                value={value}
                disabled={readOnly}
                onChange={(e) => onChange(field.name, e.target.value)}
                required={field.required}
                className={`${inputBase} cursor-pointer font-medium`}
              >
                <option value="">-- Choose Option --</option>
                {(field.options || []).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            )}

            {/* Radio */}
            {type === 'radio' && (
              <div className="space-y-2 pt-0.5">
                {(field.options || []).map((opt) => (
                  <label
                    key={opt}
                    className="flex items-center gap-2.5 cursor-pointer text-sm font-medium text-slate-700 dark:text-foreground/90 hover:text-slate-900 dark:hover:text-foreground"
                  >
                    <input
                      type="radio"
                      name={field.name}
                      value={opt}
                      checked={value === opt}
                      disabled={readOnly}
                      onChange={(e) => onChange(field.name, e.target.value)}
                      className="size-4 text-primary border-slate-300 dark:border-border focus:ring-primary"
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Checkbox */}
            {type === 'checkbox' && (
              <label className="flex items-center gap-2.5 cursor-pointer text-sm font-medium text-slate-700 dark:text-foreground/90 pt-0.5">
                <input
                  type="checkbox"
                  checked={Boolean(value)}
                  disabled={readOnly}
                  onChange={(e) => onChange(field.name, e.target.checked)}
                  className="size-4 rounded border-slate-300 dark:border-border text-primary focus:ring-primary"
                />
                <span>{field.placeholder || field.label}</span>
              </label>
            )}

            {/* File / Image upload */}
            {(type === 'file' || type === 'image') && (
              <div className="flex items-center gap-3 pt-0.5">
                <input
                  type="file"
                  id={`file-${field.name}`}
                  disabled={readOnly}
                  onChange={(e) => handleFileUpload(field.name, e)}
                  className="hidden"
                  accept={type === 'image' ? 'image/*' : '.pdf,.doc,.docx,.png,.jpg'}
                />
                <label
                  htmlFor={`file-${field.name}`}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 dark:border-border/80 bg-slate-50 dark:bg-muted hover:bg-slate-100 dark:hover:bg-muted/80 cursor-pointer text-sm font-semibold text-slate-700 dark:text-foreground transition-colors"
                >
                  <Upload className="size-4 text-primary" />
                  {values[field.name]?.fileName ? 'Replace File' : 'Choose File'}
                </label>
                {values[field.name]?.fileName && (
                  <span className="font-mono text-xs text-emerald-600 dark:text-emerald-500 font-bold truncate max-w-[200px] flex items-center gap-1 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 rounded-md px-2 py-1">
                    <FileText className="size-3.5 shrink-0" />
                    <span className="truncate">{values[field.name].fileName}</span>
                    {values[field.name].version > 1 && (
                      <span className="text-[10px] text-slate-500 dark:text-muted-foreground">
                        (v{values[field.name].version})
                      </span>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
        )
      })}

      {/* Required Verification Documents — mirrors admin preview exactly */}
      {documents.length > 0 && (
        <div className="space-y-3 pt-2 border-t border-slate-200 dark:border-border/60">
          <div className="flex items-center gap-2 pt-1">
            <Upload className="size-4 text-rose-500" />
            <span className="font-bold text-slate-800 dark:text-foreground text-sm">
              Required Verification Documents
            </span>
          </div>
          {documents.map((doc, docIdx) => {
            const docKey = `doc_${doc.id || docIdx}`
            const uploaded = values[docKey]
            return (
              <div
                key={docIdx}
                className="rounded-xl border border-dashed border-rose-300 dark:border-rose-500/40 bg-rose-50 dark:bg-rose-500/5 p-4 space-y-2"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="font-semibold text-sm text-slate-800 dark:text-foreground">
                    {doc.name}
                    {doc.required && (
                      <span className="ml-1.5 text-[10px] font-bold text-red-500 uppercase tracking-wide">
                        Required
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-muted-foreground">
                    {(doc.acceptedFormats || ['.pdf', '.jpg', '.png']).join(', ')} · Max {doc.maxMb || 5}MB
                  </p>
                </div>
                <input
                  type="file"
                  id={`doc-${docKey}`}
                  className="hidden"
                  accept={(doc.acceptedFormats || ['.pdf', '.jpg', '.png']).join(',')}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    onChange(docKey, {
                      field: doc.name,
                      fileName: file.name,
                      url: URL.createObjectURL(file),
                      uploadedAt: new Date().toISOString(),
                      version: (values[docKey]?.version || 0) + 1,
                    })
                  }}
                />
                <label
                  htmlFor={`doc-${docKey}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-300 dark:border-rose-500/40 bg-white dark:bg-background text-rose-600 dark:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 cursor-pointer text-xs font-bold transition-colors"
                >
                  <Upload className="size-3.5" />
                  {uploaded?.fileName ? (
                    <span className="text-emerald-600 dark:text-emerald-500 flex items-center gap-1">
                      <FileText className="size-3" /> {uploaded.fileName}
                    </span>
                  ) : (
                    'Select File'
                  )}
                </label>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
